import classNames from 'classnames';
import React, { useMemo } from 'react';
import { HomepageHeader, PageModule, PageModuleType, RegistrationStepType, Session } from '../../../../types/working-model';
import { eventPath, getStylingOverrides } from '../../utils';
import { MainEventBannerLayoutTypesClassic, TemplateClassNames } from '../../../../types/template-layouts';
import './main-event-banner-live.scss';
import { Tag } from '../../../general-ui/tag/tag';
import { TIconName } from '../../../general-ui/icon/icon.types';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../general-ui/button/button';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import { ParamsProps } from '../../live-event';
import { useParams } from 'react-router';
import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import { niceDateTimeTz } from '../../../../utils/utils';

interface MainEventBannerLiveProps {
	module: PageModule
	header: HomepageHeader;
	startDate: Date
	template: TemplateClassNames;
	liveSession: Session;
}

const MainEventBannerLive: React.FC<MainEventBannerLiveProps> = ({ module, header, startDate, template, liveSession }) => {

	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);
	const validRegistration = useTypedSelector(state => state.LiveEventReducer.validRegistration);
	const registrationId = useTypedSelector(state => state.LiveEventReducer.registrationId);

	const { language, eventName, uuid }: ParamsProps = useParams();

	const { content, styling_overrides } = module;
	// const { image, originalImage } = content;
	const layout_type = content?.layout_type || MainEventBannerLayoutTypesClassic.standard;
	const { t } = useTranslation(['homepage']);

	const overrides = getStylingOverrides(styling_overrides);
	const banner = eventBundle?.homepage?.modules.find((module: PageModule) => module.type === PageModuleType.main_event_banner);
	const homepageLinkImage = banner?.content.image || eventBundle?.registration_settings?.image;

	const backgroundImg = overrides.backgroundImg.backgroundImage ? overrides.backgroundImg : { backgroundImage: `url(${content.image})` };
	const sessionLink = useMemo(() => {
		const ticketingStep = eventBundle?.registration_steps?.find(step => step.type == RegistrationStepType.ticketing && step.isOn);
		let sessionHasTicket = false;

		if (ticketingStep) {
			const sessionTickets = ticketingStep.ticketing?.find(ticket => ticket.session_uuids.indexOf(liveSession.uuid) >= 0) || [];

			if (sessionTickets) {
				sessionHasTicket = true;
			}
		}

		if (eventBundle?.registration_on && !validRegistration) {
			let baseUrl = `/${eventPath(eventName, uuid)}/${language}/registration`;

			if (sessionHasTicket) {
				baseUrl = `${baseUrl}/${liveSession.uuid}`;
			}

			return baseUrl;
		} else {
			return `/${eventPath(eventName, uuid)}/${language}/session/${liveSession.uuid}`;
		}
	}, [eventBundle?.registration_steps, eventBundle?.registration_on, validRegistration, liveSession.uuid, eventName, uuid, language]);

	const getLiveSessionUrl = () => {
		if (eventBundle?.registration_on && !registrationId) {
			return `/${eventPath(eventName, uuid)}/${language}/registration`;
		} else {
			return sessionLink;
		}
	};

	return (
		<section className={classNames('live-session-banner', `live-session-banner-${layout_type}`, template)} style={backgroundImg}>
			<div className="live-session-banner-header">
				<img src={header.logo || homepageLinkImage} alt="company logo" width="48" height="48" aria-hidden="true" />
				<div>
					<TypographyItem
						className={classNames('stable', {
							'evt-caption-1': template === TemplateClassNames.Apollo,
							'evt-body-text-2': template !== TemplateClassNames.Apollo,
						})}
						tagName="time"
					>
						{niceDateTimeTz(startDate.valueOf())}
					</TypographyItem>
					<TypographyItem className="evt-heading-5" tagName="p">
						{content.title[language]}
					</TypographyItem>
				</div>
			</div>
			<div className="module-container">
				<div className="live-session-banner-tags">
					<Tag
						classNameTag={classNames('tag-container-type')}
						text="Broadcast"
						icon={"view" as TIconName}
					/>
					<Tag
						classNameTag={classNames('tag-container-live')}
						text="Live"
					/>
					{/* 
					
					need to get real time viewer metrics for this to make sense

					<Tag
						classNameTag={classNames('tag-container-views')}
						text="340"
						icon={"view" as TIconName}
					/> 
					
					*/}
				</div>
				<TypographyItem className="live-session-banner-title evt-heading-2" tagName="h1">
					{liveSession.title[language]}
				</TypographyItem>
				<Button
					tagName="a"
					url={getLiveSessionUrl()}
					classButton="primary"
					template={template}
				>{t('homepage:SESSION DETAILS')}</Button>
			</div>
		</section>
	);
};

export default MainEventBannerLive;
